import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => (
  <div className="bg-[#0a0a0a] text-white min-h-screen px-8 py-16 sm:px-7 lg:px-8">
    {/* Top Nav */}
    <nav className="mb-16 text-center">
      <Link to="/" className="inline-flex items-center gap-2 no-underline">
        <span className="text-2xl">🕵🏽</span>
        <span className="text-2xl font-bold text-[#e07a5f]">WhyDidItDie</span>
      </Link>
    </nav>

    <div className="max-w-4xl mx-auto text-center">
      {/* Hero */}
      <h1 className="text-4xl sm:text-5xl font-black mb-6 text-[#e07a5f]">
        About WhyDidItDie
      </h1>

      <p className="text-lg sm:text-xl mb-6 text-white/80 leading-relaxed">
        <em>WhyDidItDie</em> is a global marketing intelligence workspace built by{' '}
        <strong>Isabella Ibeji</strong> for people who live inside dashboards all day –
        founders, media buyers, growth teams, agencies, and operators who need
        clear answers, not more tabs.
      </p>

      <p className="text-lg sm:text-xl mb-10 text-white/80 leading-relaxed">
        Instead of jumping between scattered platforms, spreadsheets, and DMs,
        WhyDidItDie connects your data, your decisions, and your strategy in one place.
        It shows you what is happening, why it is happening, and what to do next –
        in plain language that humans (and AI) can understand.
      </p>

      {/* How it helps */}
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#e07a5f]">
        How WhyDidItDie Helps You Work
      </h2>

      <p className="text-base sm:text-lg mb-10 text-white/80 leading-relaxed text-left sm:text-center">
        🧩 <strong>See the whole picture</strong><br />
        Pull performance, patterns, and decisions into one clean view instead of
        living in ten different dashboards.
        <br /><br />

        🧠 <strong>Ask better questions</strong><br />
        “What changed this week?”, “Where is the waste?”, “Which markets should we
        enter next?” – WhyDidItDie turns those questions into structured analysis.
        <br /><br />

        📚 <strong>Keep a living memory of decisions</strong><br />
        Strategy docs, tests, learnings, and changes are captured as decision logs and
        timelines, so your team – and your future AI copilots – always know the context.
        <br /><br />

        ✍️ <strong>Create content with real context</strong><br />
        The AI Content Engine uses your performance, audiences, and goals to help you
        draft campaigns, reports, scripts, and client updates faster and smarter.
        <br /><br />

        🌍 <strong>Think beyond one market or platform</strong><br />
        Whether you are scaling a single brand or managing dozens of clients, you can
        explore new regions, channels, and opportunities with structured, data-driven
        recommendations.
      </p>

      {/* Stack overview */}
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#e07a5f]">
        What Lives Inside WhyDidItDie
      </h2>

      <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 text-left mb-10">
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-2 text-white">
            Campaign Detective
          </h3>
          <p className="text-sm text-white/75">
            Unified snapshots across ad accounts and platforms, so you can see
            what is healthy, what needs attention, and what is quietly inefficient –
            without switching tabs.
          </p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-2 text-white">
            Decision Tracker
          </h3>
          <p className="text-sm text-white/75">
            A living decision log for your marketing: what changed, who changed it,
            why it was done, and how it impacted results across time.
          </p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-2 text-white">
            AI Content Engine
          </h3>
          <p className="text-sm text-white/75">
            Context-aware AI that helps you draft campaigns, emails, reports, and
            scripts based on your goals, audiences, and performance data.
          </p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-2 text-white">
            Waste Detective
          </h3>
          <p className="text-sm text-white/75">
            Early-warning signals for budget leaks and inefficiencies, so you can
            re-allocate spend before it quietly erodes your results.
          </p>
        </div>
      </div>

      {/* Market vs Platform Intelligence */}
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#e07a5f]">
        Global Market Intelligence vs Platform Intelligence
      </h2>

      <p className="text-base sm:text-lg mb-6 text-white/80 leading-relaxed">
        These two engines work together – one helps you understand the world,
        the other helps you choose where and how to show up in it.
      </p>

      <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 text-left mb-10">
        {/* Global Market Intelligence */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-2 text-white">
            🌍 Global Market Intelligence
          </h3>
          <p className="text-sm text-white/75 mb-3">
            Real-time AI analysis of countries, industries, and regions so you can
            make confident moves in new and existing markets.
          </p>
          <ul className="text-sm text-white/75 space-y-1 list-disc list-inside">
            <li>Market entry and expansion strategies</li>
            <li>Investment and growth opportunities by region</li>
            <li>Product and positioning ideas for different markets</li>
            <li>Competitive landscape – who is active, where, and how</li>
            <li>Different strategies for different goals: scale, test, or defend</li>
          </ul>
        </div>

        {/* Platform Intelligence */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-lg font-semibold mb-2 text-white">
            📡 Platform Intelligence
          </h3>
          <p className="text-sm text-white/75 mb-3">
            An AI strategy engine that recommends the best global advertising platforms
            and go-to-market mix for your business, audience, and budget.
          </p>
          <ul className="text-sm text-white/75 space-y-1 list-disc list-inside">
            <li>Channel recommendations across global and regional platforms</li>
            <li>Budget allocation and pacing across your chosen mix</li>
            <li>High-level GTM strategies for each platform</li>
            <li>Client-ready briefs and presentation-ready summaries in minutes</li>
            <li>Strategies you can export, share, and refine with your team or AI</li>
          </ul>
        </div>
      </div>

      {/* Founder section */}
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#e07a5f]">
        Meet the Founder
      </h2>

      <p className="text-base sm:text-lg mb-6 text-white/80 leading-relaxed">
        <strong>Isabella Ibeji</strong> is a global advertising strategist and systems
        builder who has spent nearly a decade running performance marketing across
        law, finance, SaaS, healthcare, and consumer brands. She has managed tens of
        millions in ad spend, built funnel infrastructure for agencies and in-house
        teams, and taught marketers how to think in systems instead of “one more ad.”
      </p>

      <p className="text-base sm:text-lg mb-6 text-white/80 leading-relaxed">
        WhyDidItDie started as her internal toolkit: a way to connect performance
        data, human decisions, and AI in one place. Today, it is being shaped in
        public – as marketers, founders, and teams around the world plug in their
        accounts and push the product further.
      </p>

      <p className="text-base italic mb-10 text-white/70">
        Isabella still writes SQL, checks logs, and ships features herself. If
        something feels surprisingly practical inside WhyDidItDie, there is a good
        chance it came from a real client problem she has seen too many times.
      </p>

      {/* Who we serve */}
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-[#e07a5f]">
        Who WhyDidItDie Is For
      </h2>

      <p className="text-base sm:text-lg mb-10 text-white/80 leading-relaxed">
        WhyDidItDie is built for global teams who care about clarity:
        media buyers, CMOs, founders, growth leads, in-house analysts, agencies,
        and anyone who wants their marketing – and their AI – to make decisions
        with real context instead of guesswork.
      </p>

      <a
        href="https://app.whydiditdie.com"
        className="inline-block text-[#e07a5f] text-lg no-underline hover:text-[#ff6b6b] transition-colors"
      >
        ← Back to Home
      </a>
    </div>

    {/* Footer */}
    <footer className="mt-16 pt-8 border-t border-white/10">
      <div className="max-w-3xl mx-auto text-center">
        <h4 className="text-base font-bold text-white mb-4">Follow Us</h4>
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4 justify-center">
          <a
            href="https://www.instagram.com/ai_whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.326-2.633 1.301-3.608.975-.975 2.242-1.24 3.608-1.301 1.266-.058 1.646-.07 4.85-.07m0-2.163c-3.259 0-3.667.014-4.947.072-1.524.069-2.978.377-4.103 1.502-1.125 1.125-1.433 2.579-1.502 4.103-.058 1.28-.072 1.688-.072 4.947s.014 3.667.072 4.947c.069 1.524.377 2.978 1.502 4.103 1.125 1.125 2.579 1.433 4.103 1.502 1.28.058 1.688.072 4.947.072s3.667-.014 4.947-.072c1.524-.069 2.978-.377 4.103-1.502 1.125-1.125 1.433-2.579 1.502-4.103.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.069-1.524-.377-2.978-1.502-4.103-1.125-1.125-2.579-1.433-4.103-1.502-1.28-.058-1.688-.072-4.947-.072z"/>
              <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8z"/>
              <circle cx="18.406" cy="5.594" r="1.44"/>
            </svg>
            <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@ai_whydiditdie</span>
          </a>

          <a
            href="https://x.com/whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zM16.62 20.61h2.048L7.353 3.39H5.154l11.466 17.22z"/>
            </svg>
            <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@whydiditdie</span>
          </a>

          <a
            href="https://www.facebook.com/whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@whydiditdie</span>
          </a>

          <a
            href="https://www.tiktok.com/@app.whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 p-2 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-5 h-5 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69c-.93-.91-2.17-1.49-3.54-1.62v-2.1h-2.64v11.28c0 1.38-1.12 2.5-2.5 2.5s-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5c.24 0 .47.04.69.11v-2.88c-.22-.02-.44-.03-.67-.03-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5V8.64c1.33.53 2.81.84 4.31.84V6.69z"/>
            </svg>
            <span className="text-sm text-white/90 group-hover:text-[#ff6b6b]">@app.whydiditdie</span>
          </a>
        </div>
      </div>
    </footer>
  </div>
);

export default AboutPage;
